import { useState, MouseEvent } from 'react';
import { PhoneIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { useVoiceStore } from '../../../stores';
import { twilioClient } from '../twilio-client';
import { softphone } from '../services/jambonzSoftphone';

interface CallCustomerButtonProps {
  customerPhone: string;
  customerName: string;
  /**
   * Attach the call to an existing conversation. Optional — a cold outbound
   * call (e.g. from the Customers page) has none; the Twilio webhook creates a
   * voice session server-side when it's absent.
   */
  conversationId?: string;
  /** Icon-only variant for dense contexts like table rows. */
  compact?: boolean;
  className?: string;
  /** When the "Call" label shows; the chat header ties it to its own width. */
  labelClassName?: string;
}

/**
 * Places an outbound call to a customer. Picks whichever softphone is
 * registered — SIP (Jambonz) preferred, else Twilio — and pops the globally
 * mounted <InCallPanel/> via voiceStore. Self-gates: toasts if already on a
 * call or no softphone is ready. Reused by the chat header and the Customers
 * page.
 */
export default function CallCustomerButton({
  customerPhone,
  customerName,
  conversationId = '',
  compact = false,
  className,
  labelClassName = 'hidden sm:inline',
}: CallCustomerButtonProps) {
  const activeCall = useVoiceStore((s) => s.activeCall);
  const setActive = useVoiceStore((s) => s.setActive);
  const [calling, setCalling] = useState(false);

  const handleClick = async (e: MouseEvent) => {
    // In dense contexts (table rows) the parent row is clickable; never let a
    // call click bubble up to it.
    e.stopPropagation();
    if (calling) return;
    if (activeCall) {
      toast.error('Already on a call — end it first');
      return;
    }
    // Use whichever softphone is registered — SIP (Jambonz) preferred, else Twilio.
    const sipReady = softphone.getState() === 'registered';
    const twilioReady = twilioClient.getDevice()?.state === 'registered';
    if (!sipReady && !twilioReady) {
      toast.error('Voice not ready — make sure calling is configured and you are available');
      return;
    }
    if (!window.confirm(`Call ${customerName} at ${customerPhone}?`)) return;

    setCalling(true);
    try {
      if (sipReady) {
        // SIP: the SipSoftphone (onOutgoing) surfaces the in-call UI itself.
        const call = await softphone.dial(customerPhone);
        if (!call) {
          toast.error('Could not place call');
          return;
        }
      } else {
        const call = await twilioClient.startOutboundCall(customerPhone, conversationId);
        if (!call) {
          toast.error('Could not place call');
          return;
        }
        setActive({
          call: {
            id: 'outbound', // server assigns the real Call row on webhook hit
            conversationId,
            customerName,
            customerPhone,
            transport: 'twilio',
            ringingAt: new Date().toISOString(),
            offerSdp: '',
          },
          transport: 'twilio',
          twilioCall: call,
          answeredAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.warn('[call-customer] outbound call failed:', err);
      toast.error('Could not place call');
    } finally {
      setCalling(false);
    }
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={calling}
        className={cn(
          'rounded-pill p-1.5 text-brand transition-colors hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        title={`Call ${customerName}`}
      >
        <PhoneIcon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={calling}
      className={cn(
        'flex items-center gap-1.5 rounded-pill p-1.5 text-sm font-medium text-brand hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50 sm:px-3',
        className
      )}
      title={`Call ${customerName}`}
    >
      <PhoneIcon className="h-4 w-4" />
      <span className={labelClassName}>{calling ? 'Calling…' : 'Call'}</span>
    </button>
  );
}
